import { Injectable, inject } from '@angular/core';
import { AuthService } from '@openmfp/portal-ui-lib';
import { Observable } from 'rxjs';

export interface RagSource {
  rank: number;
  source: string;
  snippet: string;
  score: number | null;
}

export type Scope = 'external' | 'internal' | 'all';

export type RagStreamEvent =
  | { type: 'metadata'; question: string; sources: RagSource[] }
  | { type: 'chunk'; content: string }
  | { type: 'done' }
  | { type: 'error'; message: string };

@Injectable({ providedIn: 'root' })
export class RagChatService {
  private baseUrl = '';
  private authService = inject(AuthService);

  setBaseUrl(url: string): void {
    this.baseUrl = url.replace(/\/$/, '');
  }

  queryStream(
    question: string,
    scope: Scope = 'external',
  ): Observable<RagStreamEvent> {
    return new Observable((subscriber) => {
      const controller = new AbortController();
      const token = this.authService.getToken();

      fetch(`${this.baseUrl}/api/query/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ question, scope }),
        signal: controller.signal,
      })
        .then(async (response) => {
          if (!response.ok) {
            subscriber.next({
              type: 'error',
              message: `HTTP ${response.status}`,
            });
            subscriber.complete();
            return;
          }

          const reader = response.body?.getReader();
          if (!reader) {
            subscriber.next({ type: 'error', message: 'No response body' });
            subscriber.complete();
            return;
          }

          const decoder = new TextDecoder();
          let buffer = '';

          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop() || '';

            for (const line of lines) {
              if (line.startsWith('data: ')) {
                try {
                  const event: RagStreamEvent = JSON.parse(line.slice(6));
                  subscriber.next(event);
                  if (event.type === 'done' || event.type === 'error') {
                    subscriber.complete();
                    return;
                  }
                } catch {
                  // skip malformed lines
                }
              }
            }
          }

          subscriber.complete();
        })
        .catch((err) => {
          if (err.name !== 'AbortError') {
            subscriber.next({ type: 'error', message: err.message });
            subscriber.complete();
          }
        });

      return () => {
        controller.abort();
      };
    });
  }
}
